import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './LandingPage.css';
import LandingWhatWeDo from './LandingWhatWeDo';
import LandingSteps from './LandingSteps';
import MarketingNav from './MarketingNav';
import LandingShot from './LandingShot';
import LandingStats from './LandingStats';
import { keepPendingPrompt } from '../utils/pendingPrompt';
import usePageMeta from '../hooks/usePageMeta';
import useScrollToHash from '../hooks/useScrollToHash';
import { useAuth } from './AuthContext';

const ROTATING_SUGGESTIONS = [
  'Ask anything about jobs, resumes, or applications...',
  'Find jobs that fit my resume...',
  'Tailor my resume to a job description...',
  'Prep me for an upcoming interview...',
  'Apply to my saved jobs in my browser...'
];

const LandingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const currentUser = auth?.currentUser;
  const deleted = location.state?.accountDeleted;
  const [prompt, setPrompt] = useState('');
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  usePageMeta('landing');
  // The footer sends signed-out visitors to /#jobs, /#apply and the rest.
  useScrollToHash();

  // Signed out, every product link on this page goes to sign-up first, the
  // way the hero's own buttons do; signed in, straight to the page.
  const go = (path) => (currentUser ? path : '/register');

  useEffect(() => {
    if (prompt) return undefined;
    const interval = setInterval(() => {
      setIsFading(true);
      const timer = setTimeout(() => {
        setSuggestionIndex((prev) => (prev + 1) % ROTATING_SUGGESTIONS.length);
        setIsFading(false);
      }, 260);
      return () => clearTimeout(timer);
    }, 3800);
    return () => clearInterval(interval);
  }, [prompt]);

  // What they typed is kept through sign-up and asked as their first
  // question once they are in (utils/pendingPrompt.js; the home chat asks it).
  // An empty box keeps nothing: the moving suggestion is an example, not theirs.
  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    keepPendingPrompt(prompt);
    navigate(currentUser ? '/dashboard' : '/register');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="landing-page claude-theme apple-redesign">
      {deleted && (
        <div role="status" className="landing-notice">
          {/* True since 2026-09-29: a daily pass retries an unfinished
              deletion (accountDeletion.js finishIncompleteDeletions), and
              lists it on the admin page for a person after five tries.
              Before, nothing ever finished one. */}
          {location.state?.complete === false
            ? 'Your account is deleted. One step could not finish on the spot; we retry it every day and finish it within 30 days.'
            : 'Your account and everything in it have been deleted.'}
        </div>
      )}
      {/* 5. Effortless Navigation & Wayfinding: Shallow Hierarchies */}
      <MarketingNav />

      {/* 1. Sell the Experience Before the Specs & 4. White Space as an Active Tool */}
      <section className="apple-hero">
        <div className="container-inner">
          <div className="apple-hero-text lp-rise">
            <h1 className="apple-title">
              <span>Apply to the right jobs.</span>{' '}
              <span className="lp-title-second">Skip the busywork.</span>
            </h1>
            <p className="apple-subtitle">
              We find the jobs, tailor your resume, and fill the applications. You press Submit.
            </p>
            {/* Exactly matching workspace chat composer with moving suggestions */}
            <div className="lp-search-container" role="region" aria-label="Interactive prompt demo">
              <form onSubmit={handleSubmit} className="lp-search-card">
                <div className="lp-search-input-wrap">
                  {!prompt && (
                    <span
                      className={`lp-moving-suggestion ${isFading ? 'is-fading' : ''}`}
                      aria-hidden="true"
                    >
                      {ROTATING_SUGGESTIONS[suggestionIndex]}
                    </span>
                  )}
                  <textarea
                    className="lp-search-textarea"
                    rows={2}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    onKeyDown={handleKeyDown}
                    aria-label="Ask anything about jobs, resumes, or applications"
                  />
                </div>

                <div className="lp-search-bottom">
                  <div className="lp-search-tools">
                    <button
                      type="button"
                      className="lp-composer-icon-btn"
                      onClick={() => navigate(currentUser ? '/dashboard' : '/register')}
                      title="Import a resume"
                      aria-label="Import a resume"
                    >
                      <span className="material-symbols-rounded" aria-hidden="true">add</span>
                    </button>
                    <span className="lp-model-pill">
                      <span className="material-symbols-rounded" style={{ fontSize: '15px' }} aria-hidden="true">auto_awesome</span>
                      <span>Auto</span>
                    </span>
                  </div>

                  <button
                    type="submit"
                    className="lp-send-btn"
                    aria-label={currentUser ? 'Open workspace' : 'Send'}
                    title={currentUser ? 'Open workspace' : 'Send'}
                  >
                    <span className="material-symbols-rounded" aria-hidden="true">arrow_upward</span>
                  </button>
                </div>
              </form>

              {/* Reassurance Strip */}
              <div className="lp-hero-trust">
                <span>Free to start. No card needed.</span>
                <span className="lp-trust-dot" aria-hidden="true">•</span>
                <Link to="/register" className="lp-see-how" aria-label="Start free">
                  Start free <span aria-hidden="true">→</span>
                </Link>
                <span className="lp-trust-dot" aria-hidden="true">•</span>
                <a href="#what-we-do" className="lp-see-how">
                  See what we do <span aria-hidden="true">↓</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Three numbers that can be checked (copy/product.js STATS). */}
      <LandingStats />

      {/* 2. What we do: four big lines, one lit at a time. */}
      <LandingWhatWeDo />

      {/* 3. Everything in one place: four calm cards, each with one drawing,
          one sentence and one quiet link to the real page. Signed out, each
          link goes to sign-up first, like every other button on this page.
          The ids are where the footer's Product links land for a visitor
          (Footer.js publicTo). */}
      <section className="lp-one" aria-labelledby="one-title">
        <div className="lp-wrap">
          <h2 id="one-title" className="lp-display">Everything for your job search, in one place</h2>
          <div className="lp-one-grid">
            <article className="lp-card" id="jobs">
              <div className="lp-card-visual"><LandingShot name="jobs" mark={false} /></div>
              <div className="lp-card-body">
                <h3 className="lp-sr">Jobs</h3>
                <p className="lp-card-text"><strong>Jobs that fit,</strong> ranked against your resume.</p>
                <Link to={go('/for-you')} className="lp-more">Browse jobs<span aria-hidden="true"> →</span></Link>
              </div>
            </article>

            <article className="lp-card" id="design-studio">
              <div className="lp-card-visual"><LandingShot name="resume" mark={false} /></div>
              <div className="lp-card-body">
                <h3 className="lp-sr">Resume</h3>
                <p className="lp-card-text"><strong>A resume for every job.</strong> Nothing made up.</p>
                <Link to={go('/design-studio')} className="lp-more">Open the resume editor<span aria-hidden="true"> →</span></Link>
              </div>
            </article>

            <article className="lp-card" id="apply">
              <div className="lp-card-visual"><LandingShot name="autoapply" mark={false} /></div>
              <div className="lp-card-body">
                <h3 className="lp-sr">Auto-apply</h3>
                <p className="lp-card-text"><strong>Applications filled for you,</strong> in your own browser.</p>
                <Link to={go('/job-agent')} className="lp-more">Set up Auto-apply<span aria-hidden="true"> →</span></Link>
              </div>
            </article>

            <article className="lp-card" id="interview-coach">
              <div className="lp-card-visual"><LandingShot name="coach" mark={false} /></div>
              <div className="lp-card-body">
                <h3 className="lp-sr">Interview coach</h3>
                <p className="lp-card-text"><strong>Walk in prepared,</strong> with a coach that knows your stories.</p>
                <Link to={go('/interview-coach')} className="lp-more">Practice an interview<span aria-hidden="true"> →</span></Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* 4. One job, start to finish, as four tabs. */}
      <LandingSteps />

      {/* 5. Wayfinding & Clear Knowledge */}
      <section className="lp-answers" id="faq" aria-label="Before you start">
        <div className="container-inner">
          <h2 className="text-center">A few things worth knowing.</h2>
          <div className="lp-accordion-list">
            <details>
              <summary>
                <span>Does it apply automatically?</span>
                <span className="material-symbols-rounded lp-chevron" aria-hidden="true">expand_more</span>
              </summary>
              <p>You choose. Review and submit applications yourself, or enable Auto Apply for supported jobs. Filling happens through the extension in your browser.</p>
            </details>
            <details>
              <summary>
                <span>Can I edit things myself?</span>
                <span className="material-symbols-rounded lp-chevron" aria-hidden="true">expand_more</span>
              </summary>
              <p>Yes. Chat and the workspace work together. You can review jobs, edit your resume, and check applications directly.</p>
            </details>
            <details>
              <summary>
                <span>What can I try for free?</span>
                <span className="material-symbols-rounded lp-chevron" aria-hidden="true">expand_more</span>
              </summary>
              <p>Find jobs, work on your resume, and prepare for interviews. Paid plans add applications submitted for you. <Link to="/pricing">Compare plans</Link>.</p>
            </details>
          </div>
        </div>
      </section>

      {/* Aspirational Hook & Cinematic Call to Action */}
      <section className="apple-cta">
        <div className="container-inner">
          <h2 className="apple-cta-title">Your next move starts here.</h2>
          <p className="lp-closing-copy">Bring your resume. Tell us what’s next. Let’s get to work.</p>
          <div className="apple-cta-actions">
            <Link to="/pricing" className="lp-simple-pricing">Explore plans</Link>
            <Link to="/register" className="apple-btn-large">Start free</Link>
          </div>
          <p className="lp-cta-note">No card needed.</p>
        </div>
      </section>

      {/* No footer here. This page carried its own, with a different shape
          and a shorter set of links than the site footer, so the two
          disagreed about what the product offered. App.js renders the one in
          components/Footer.js under every page that has room for it, this
          one included. */}
    </div>
  );
};

export default LandingPage;
